import React, { useState, useEffect } from "react";
import { getProofImage } from "../API/getImage";
import States from "../Utilities/state.json";
import "./view.css";
import { Autocomplete, TextField } from "@mui/material";
import countries from "../Utilities/country.json";

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

function toTitleCase(value) {
  if (!value) return "";
  return String(value).replace(/\w\S*/g, (txt) =>
    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
}

// DOB comes through as "1993-03-13 00:00:00.0" (or similar). Render DD-MM-YYYY.
// Falls back to the raw value if it doesn't match the expected shape.
function formatDob(value) {
  if (!value) return "—";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return value;
  const [, year, month, day] = match;
  return `${day}-${month}-${year}`;
}

// Input sanitisers — unchanged behaviour, just hoisted out of the component.
const sanitizeNumeric = (raw) => (raw ? raw.replace(/[^0-9]+$/gi, "") : "");
const sanitizeAlpha = (raw) => (raw ? raw.replace(/[^A-Z]+$/gi, "") : "");
const sanitizeAlphaNumericWithSpl = (raw) =>
  raw ? raw.replace(/[^0-9a-zA-Z-_\s/\\]+$/gm, " ") : "";
const sanitizeAlphaNumeric = (raw) =>
  raw ? raw.replace(/[^0-9a-zA-Z]+$/gm, "").toUpperCase() : "";

/* ------------------------------------------------------------------ */
/* Source helpers                                                     */
/* getProofImage now returns a ready-to-render data: URI (it gunzips  */
/* and labels the correct MIME). The component just renders it; the   */
/* old client-side gunzip/decode path has been removed.               */
/* ------------------------------------------------------------------ */

const b64ToBytes = (b64) => {
  const bin = atob(b64);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
};

// Pulls the proof source out of whatever getProofImage returns:
// a ready data: URI string, an array of them, or a wrapper object.
const extractProof = (result) => {
  if (Array.isArray(result)) return result;
  if (result && typeof result === "object") {
    return result.fileUrl || result.image || "";
  }
  return result;
};

// Reads a source's type without trusting a hardcoded label.
const classifySrc = (raw) => {
  if (typeof raw !== "string" || !raw) return { uri: raw, isPdf: false };
  const src = raw.trim();
  if (src.startsWith("data:")) {
    return { uri: src, isPdf: src.startsWith("data:application/pdf") };
  }
  if (src.startsWith("http") || src.startsWith("/")) {
    return { uri: src, isPdf: src.toLowerCase().endsWith(".pdf") };
  }
  return { uri: src, isPdf: false };
};

export const Viewproof = ({
  rowData,
  onManualStatusChange,
  onRejectReasonChange,
  onRejectCommentsChange,
}) => {
  /* ---------------------------------------------------------------- */
  /* State                                                            */
  /* ---------------------------------------------------------------- */
  const [rejectReason, setRejectReason] = useState("");
  const [rejectComments, setRejectComments] = useState("");
  const [approvalStatus, setApprovalStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [transactionImage, setTransactionImage] = useState("");

  // FaceMatch images come from the same getProofImage response:
  // selfieImage = submitted photo (left), image = reference proof (right).
  const [faceMatchSubmitted, setFaceMatchSubmitted] = useState("");
  const [faceMatchReference, setFaceMatchReference] = useState("");

  // Left/main proof zoom+rotate.
  const [zoomFactor, setZoomFactor] = useState(1);
  const [rotationAngle, setRotationAngle] = useState(0);
  // Reference (right) zoom+rotate — independent of the left.
  const [refZoom, setRefZoom] = useState(1);
  const [refRotate, setRefRotate] = useState(0);

  const [accNo, setAccNo] = useState(rowData.accountNo);
  const [ifscCode, setIfscCode] = useState(rowData.ifscCode);
  const [bankName, setBankName] = useState(rowData.bankName);
  const [branchName, setBranchName] = useState(rowData.branchName);
  const [holderName, setHolderName] = useState(rowData.accountHolderName);
  const [add1, setAdd1] = useState(rowData.address1);
  const [add2, setAdd2] = useState(rowData.address2);
  const [add3, setAdd3] = useState(rowData.address3);
  const [addCity, setAddCity] = useState(rowData.city);
  const [pincode, setPincode] = useState(rowData.pincode);
  const [msgValue] = useState(rowData.msgId);
  const [state, setState] = useState(rowData.state);
  // Initialise to null (not undefined) so the Autocomplete stays controlled
  // for the component's lifetime — silences the MUI uncontrolled warning.
  const [country, setCountry] = useState(rowData.country ?? null);


  const type = (rowData.serviceType || "").toUpperCase();
  const isNeft = type === "NEFT";
  const isAddress =
    type === "ADDRESS" ||
    type === "COMMUNICATION_ADDRESS" ||
    type === "EMAIL_KYC" ||
    type === "MOBILE_KYC";
  const isFaceMatch = type === "FACEMATCH";
  const isKnownType = isNeft || isAddress || isFaceMatch;
  const status = rowData.manualStatus;
  const isDecided = status === "REJECTED" || status === "APPROVED";

  /* ---------------------------------------------------------------- */
  /* Image fetch                                                      */
  /* getProofImage returns a ready data: URI (or wrapper object). We    */
  /* just store it. For FaceMatch the wrapper carries selfieImage       */
  /* (submitted) and image (reference).                                 */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    let cancelled = false;
    const fetchImage = async () => {
      if (!rowData.transactionId || !rowData.eiaNo) return;
      setIsLoading(true);
      try {
        const result = await getProofImage(rowData.transactionId, rowData.eiaNo);

        if (
          isFaceMatch &&
          result &&
          typeof result === "object" &&
          !Array.isArray(result)
        ) {
          if (!cancelled) {
            setFaceMatchSubmitted(result.selfieImage || "");
            setFaceMatchReference(result.image || "");
          }
          return;
        }

        const proof = extractProof(result);
        if (!cancelled) setTransactionImage(proof);
      } catch (error) {
        console.error("Error fetching image:", error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchImage();
    return () => {
      cancelled = true;
    };
  }, [rowData.transactionId, rowData.eiaNo, isFaceMatch]);

  /* ---------------------------------------------------------------- */
  /* Image controls — left/main                                       */
  /* ---------------------------------------------------------------- */
  const handleZoomIn = () => setZoomFactor((z) => z + 0.1);
  const handleZoomOut = () => setZoomFactor((z) => Math.max(0.1, z - 0.1));
  const handleZoomReset = () => {
    setZoomFactor(1);
    setRotationAngle(0);
  };
  const handleRotate = () => setRotationAngle((a) => a + 90);

  /* ----- Image controls — reference/right (FaceMatch only) ----- */
  const refZoomIn = () => setRefZoom((z) => z + 0.1);
  const refZoomOut = () => setRefZoom((z) => Math.max(0.1, z - 0.1));
  const refReset = () => {
    setRefZoom(1);
    setRefRotate(0);
  };
  const refRotateFn = () => setRefRotate((a) => a + 90);

  const base64ToBlob = (base64, type = "application/octet-stream") => {
    const bytes = b64ToBytes(base64);
    return new Blob([bytes], { type });
  };

  // Opens a given source (defaults to the main proof) in a new tab.
  const openInNewTab = (rawSrc) => {
    const source = rawSrc || transactionImage;
    if (!source || typeof source !== "string") return;
    const { uri, isPdf } = classifySrc(source);
    try {
      const w = window.open("");
      if (!w) throw new Error("Failed to open a new tab.");
      if (isPdf && uri.startsWith("data:")) {
        const blob = base64ToBlob(uri.split(",")[1], "application/pdf");
        const url = URL.createObjectURL(blob);
        w.document.write(
          `<iframe width='100%' height='100%' src='${url}'></iframe>`
        );
      } else if (isPdf) {
        w.document.write(
          `<iframe width='100%' height='100%' src='${uri}'></iframe>`
        );
      } else {
        const imageType = uri.startsWith("data:image/png") ? "image/png" : "image/jpeg";
        w.document.write(
          `<embed width="100%" height="100%" src="${uri}" type="${imageType}" />`
        );
      }
    } catch (error) {
      console.error("Error opening proof in new tab:", error);
    }
  };

  const handleOpenPdfInNewTab = () =>
    openInNewTab(isFaceMatch ? faceMatchSubmitted : transactionImage);

  /* ---------------------------------------------------------------- */
  /* Reject / approve handlers                                        */
  /* ---------------------------------------------------------------- */
  const handleRejectReasonChange = (event) => {
    const reason = event.target.value;
    setRejectReason(reason);
    onRejectReasonChange(reason);
  };

  const handleRejectCommentsChange = (event) => {
    const comments = event.target.value;
    setRejectComments(comments);
    onRejectCommentsChange(comments);
  };

  // NOTE: preserves the original 16-arg positional signature exactly.
  const handleApprovalChange = (event) => {
    const value = event.target.value;
    setApprovalStatus(value);
    const manualStatus = value !== "reject" ? "Approved" : "Rejected";

    onManualStatusChange(
      manualStatus,
      add1,
      add2,
      add3,
      addCity,
      pincode,
      state,
      country,
      accNo,
      ifscCode,
      bankName,
      branchName,
      msgValue,
      rowData.kycProofId,
      rowData.poaCode,
      rowData.pobCode
    );
  };

  const handleState = (event) => setState(event.target.value);

  // Generic onChange wrapper that sanitises then truncates.
  const handleSanitized = (sanitizer, setter, max) => (e) => {
    const clean = sanitizer(e.target.value);
    if (e.target.value !== clean) e.target.value = clean;
    setter(clean.slice(0, max));
  };

  /* ---------------------------------------------------------------- */
  /* Render: proof image                                              */
  /* ---------------------------------------------------------------- */
  const imageStyle = {
    transform: `scale(${zoomFactor}) rotate(${rotationAngle}deg)`,
    transformOrigin: "center center",
  };
  const refImageStyle = {
    transform: `scale(${refZoom}) rotate(${refRotate}deg)`,
    transformOrigin: "center center",
  };

  // Renders an image OR pdf source with the given transform style.
  const renderSrc = (raw, alt, style, emptyMsg) => {
    if (!raw) return <span className="vp-stage-empty">{emptyMsg}</span>;
    const { uri, isPdf } = classifySrc(raw);
    return isPdf ? (
      <embed
        title={alt}
        src={`${uri}#toolbar=0&sidebar=0`}
        className="vp-proof-image"
        style={style}
      />
    ) : (
      <img src={uri} alt={alt} className="vp-proof-image" style={style} />
    );
  };

  const renderProof = () => {
    if (isLoading) return <span className="vp-stage-empty">Loading proof…</span>;

    // FaceMatch: SUBMITTED photo (selfie) in the main proof stage (left).
    if (isFaceMatch) {
      return renderSrc(
        faceMatchSubmitted,
        "Submitted photo",
        imageStyle,
        "Submitted photo not available"
      );
    }

    if (Array.isArray(transactionImage)) {
      return (
        <div className="vp-image-container">
          {transactionImage.map((src, index) => (
            <React.Fragment key={index}>
              {renderSrc(src, `Proof ${index + 1}`, imageStyle, "Proof not available")}
            </React.Fragment>
          ))}
        </div>
      );
    }

    if (transactionImage && typeof transactionImage === "string") {
      return renderSrc(transactionImage, "Proof", imageStyle, "Proof not available");
    }

    return <span className="vp-stage-empty">Proof not available</span>;
  };

  /* ---------------------------------------------------------------- */
  /* Render: current details panel                                    */
  /* ---------------------------------------------------------------- */
  const renderCurrentDetails = () => {
    if (isNeft) {
      return (
        <section className="vp-section">
          <h2 className="vp-section-title">Current bank</h2>
          <dl className="vp-readonly-grid">
            <dt>Account no.</dt><dd>{rowData.currentAccountNo || "—"}</dd>
            <dt>IFSC code</dt><dd>{rowData.currentIfscCode || "—"}</dd>
            <dt>Bank name</dt><dd>{rowData.currentBankName || "—"}</dd>
            <dt>Branch name</dt><dd>{rowData.currentBranchName || "—"}</dd>
          </dl>
        </section>
      );
    }
    if (isAddress) {
      return (
        <section className="vp-section">
          <h2 className="vp-section-title">Current address</h2>
          <dl className="vp-readonly-grid">
            <dt>Address line 1</dt><dd>{rowData.currentAddress1 || "—"}</dd>
            <dt>Address line 2</dt><dd>{rowData.currentAddress2 || "—"}</dd>
            <dt>Address line 3</dt><dd>{rowData.currentAddress3 || "—"}</dd>
            <dt>City</dt><dd>{rowData.currentCity || "—"}</dd>
            <dt>State</dt><dd>{rowData.currentState || "—"}</dd>
            <dt>Pincode</dt><dd>{rowData.currentPincode || "—"}</dd>
            <dt>Country</dt><dd>{rowData.currentCountry || "—"}</dd>
          </dl>
        </section>
      );
    }
    return null;
  };

  /* ---------------------------------------------------------------- */
  /* Render: editable proof forms                                     */
  /* ---------------------------------------------------------------- */
  const renderAddressForm = () => (
    <section className="vp-section">
      <h2 className="vp-section-title">Details fetched from the address proof</h2>
      <div className="vp-field">
        <label>Address line 1 <span className="vp-req">*</span></label>
        <input
          type="text"
          value={add1 || ""}
          onChange={handleSanitized(sanitizeAlphaNumericWithSpl, setAdd1, 45)}
          required
        />
      </div>
      <div className="vp-field">
        <label>Address line 2</label>
        <input
          type="text"
          value={add2 || ""}
          onChange={handleSanitized(sanitizeAlphaNumericWithSpl, setAdd2, 45)}
        />
      </div>
      <div className="vp-field">
        <label>Address line 3</label>
        <input
          type="text"
          value={add3 || ""}
          onChange={handleSanitized(sanitizeAlphaNumericWithSpl, setAdd3, 45)}
        />
      </div>
      <div className="vp-field">
        <label>City <span className="vp-req">*</span></label>
        <input
          type="text"
          value={addCity || ""}
          onChange={handleSanitized(sanitizeAlpha, setAddCity, 15)}
        />
      </div>
      <div className="vp-field-row">
        <div className="vp-field">
          <label>Pin code <span className="vp-req">*</span></label>
          <input
            type="text"
            value={pincode || ""}
            onChange={handleSanitized(sanitizeNumeric, setPincode, 6)}
          />
        </div>
        <div className="vp-field">
          <label>State <span className="vp-req">*</span></label>
          <select onChange={handleState} defaultValue={toTitleCase(state)}>
            {States.map((s, i) => (
              <option key={i} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="vp-field">
        <label>Country <span className="vp-req">*</span></label>
        <Autocomplete
          disablePortal
          options={countries}
          value={country}
          onInputChange={(_, newValue) => setCountry(newValue)}
          sx={{ width: 300 }}
          renderInput={(params) => <TextField {...params} />}
        />
      </div>
    </section>
  );

  const renderBankForm = () => (
    <section className="vp-section">
      <h2 className="vp-section-title">Details fetched from the cheque leaf</h2>
      <div className="vp-field">
        <label>Account holder name (from bank) <span className="vp-req">*</span></label>
        <input type="text" value={holderName || ""} onChange={(e) => setHolderName(e.target.value)} readOnly />
      </div>
      <div className="vp-field">
        <label>Account number <span className="vp-req">*</span></label>
        <input
          type="text"
          value={accNo || ""}
          onChange={handleSanitized(sanitizeNumeric, setAccNo, 20)}
        />
      </div>
      <div className="vp-field">
        <label>IFSC code <span className="vp-req">*</span></label>
        <input
          type="text"
          value={ifscCode || ""}
          onChange={handleSanitized(sanitizeAlphaNumeric, setIfscCode, 13)}
        />
      </div>
      <div className="vp-field">
        <label>Bank name <span className="vp-req">*</span></label>
        <input type="text" value={bankName || ""} onChange={(e) => setBankName(e.target.value)} readOnly />
      </div>
      <div className="vp-field">
        <label>Branch name <span className="vp-req">*</span></label>
        <input type="text" value={branchName || ""} onChange={(e) => setBranchName(e.target.value)} readOnly />
      </div>
    </section>
  );

  /* ---------------------------------------------------------------- */
  /* Render: decision controls (non-FaceMatch keep inline dropdown)    */
  /* ---------------------------------------------------------------- */
  const renderDecision = () => {
    if (status === "REJECTED") {
      return (
        <section className="vp-section vp-decided">
          <p><span className="vp-label">Manual status:</span> <span className="vp-badge vp-badge-reject">{status}</span></p>
          <p><span className="vp-label">Reject reason:</span> {rowData.rejectReason}</p>
          <p><span className="vp-label">Reject comments:</span> {rowData.rejectComments}</p>
        </section>
      );
    }
    if (status === "APPROVED") {
      return (
        <section className="vp-section vp-decided">
          <p><span className="vp-label">Manual status:</span> <span className="vp-badge vp-badge-approve">{status}</span></p>
        </section>
      );
    }

    // Pending. Only show approve/reject for service types we can submit.
    if (!isKnownType) return null;

    // FaceMatch's decision UI is rendered in the footer (TransactionModal).
    if (isFaceMatch) return null;

    return (
      <>
        <div className="vp-decision">
          <label className="vp-radio">
            <input
              type="radio"
              value="approve"
              checked={approvalStatus === "approve"}
              onChange={handleApprovalChange}
            />
            Approve
          </label>
          <label className="vp-radio">
            <input
              type="radio"
              value="reject"
              checked={approvalStatus === "reject"}
              onChange={handleApprovalChange}
            />
            Reject
          </label>
        </div>

        {approvalStatus === "reject" && (
          <div className="vp-reject-extra">
            <div className="vp-field">
              <label>Reject reason</label>
              <select value={rejectReason} onChange={handleRejectReasonChange}>
                <option value="">Select a reason</option>
                <option value="proof Not valid">Proof not clear</option>
                <option value="Name missmatch with our Record">Name mismatch with our record</option>
                <option value="Name given and the name on proof mismatch">Name given and name on proof mismatch</option>
                <option value="Submitted proof not valid">Submitted proof not valid</option>
                <option value="other">Other</option>
              </select>
            </div>
            {rejectReason === "other" && (
              <div className="vp-field">
                <label>Reject reason (details)</label>
                <input type="text" value={rejectComments} onChange={handleRejectCommentsChange} />
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  // Whether the pinned decision zone should render at all.
  const showDecisionZone = isDecided || (isKnownType && !isFaceMatch);

  /* ---------------------------------------------------------------- */
  /* Render                                                           */
  /* ---------------------------------------------------------------- */
  return (
    <div className={`vp-root${isFaceMatch ? " vp-root-facematch" : ""}`}>
      {isLoading && (
        <div className="vp-loader-overlay"><div className="vp-loader" /></div>
      )}

      <header className="vp-header">
        <div className="vp-header-item">
          <span className="vp-header-label">Name</span>
          <span className="vp-header-value" title={rowData.customerName || ""}>{rowData.customerName || "—"}</span>
        </div>
        <div className="vp-header-item">
          <span className="vp-header-label">EIA number</span>
          <span className="vp-header-value" title={rowData.eiaNo || ""}>{rowData.eiaNo || "—"}</span>
        </div>
        <div className="vp-header-item">
          <span className="vp-header-label">DOB</span>
          <span className="vp-header-value">{formatDob(rowData.dob)}</span>
        </div>
        <div className="vp-header-item vp-header-item-wide">
          <span className="vp-header-label">Transaction ID</span>
          <span className="vp-header-value" title={rowData.transactionId || ""}>{rowData.transactionId || "—"}</span>
        </div>
      </header>

      <div className="vp-body">
        {/* Proof stage — for FaceMatch this is the SUBMITTED (selfie) photo */}
        <div className="vp-proof-pane">
          <div className="vp-proof-frame">{renderProof()}</div>
          <div className="vp-zoom-bar">
            <button onClick={handleZoomIn} aria-label="Zoom in"><i className="fa-solid fa-magnifying-glass-plus" /></button>
            <button onClick={handleZoomOut} aria-label="Zoom out"><i className="fa-solid fa-magnifying-glass-minus" /></button>
            <button onClick={handleZoomReset} aria-label="Reset view"><i className="fa-solid fa-rectangle-xmark" /></button>
            <button onClick={handleRotate} aria-label="Rotate"><i className="fa-solid fa-camera-rotate" /></button>
          </div>
          <div className="vp-proof-meta">
            <span>
              {isFaceMatch ? "Submitted photo" : "Service"}: <strong>{isFaceMatch ? (rowData.serviceType || "FaceMatch") : (rowData.serviceType || "—")}</strong>
              {!isFaceMatch && rowData.documentType ? ` · ${rowData.documentType}` : ""}
            </span>
            <button className="vp-link-btn" onClick={handleOpenPdfInNewTab}>Open in new tab</button>
          </div>
        </div>

        {/* For FaceMatch: right side is the REFERENCE image/pdf with its own
            zoom/rotate toolbar. For everyone else: the detail rail. */}
        {isFaceMatch ? (
          <div className="vp-proof-pane vp-reference-pane">
            <div className="vp-proof-frame">
              {renderSrc(
                faceMatchReference,
                "Reference proof",
                refImageStyle,
                "Reference proof not available"
              )}
            </div>
            <div className="vp-zoom-bar">
              <button onClick={refZoomIn} aria-label="Zoom in"><i className="fa-solid fa-magnifying-glass-plus" /></button>
              <button onClick={refZoomOut} aria-label="Zoom out"><i className="fa-solid fa-magnifying-glass-minus" /></button>
              <button onClick={refReset} aria-label="Reset view"><i className="fa-solid fa-rectangle-xmark" /></button>
              <button onClick={refRotateFn} aria-label="Rotate"><i className="fa-solid fa-camera-rotate" /></button>
            </div>
            <div className="vp-proof-meta">
              <span>Reference proof</span>
              <button className="vp-link-btn" onClick={() => openInNewTab(faceMatchReference)}>Open in new tab</button>
            </div>
          </div>
        ) : (
          <div className="vp-detail-pane">
            <div className="vp-rail-scroll">
              {renderCurrentDetails()}

              {isAddress && !isDecided && renderAddressForm()}
              {isNeft && !isDecided && renderBankForm()}

              {!isKnownType && !isDecided && (
                <section className="vp-section vp-unconfigured">
                  <h2 className="vp-section-title">Review not configured</h2>
                  <p>
                    The <strong>{rowData.serviceType || "unknown"}</strong> service type
                    doesn&rsquo;t have a review form set up yet. Approve and reject are disabled
                    to prevent submitting an incomplete decision. Contact the admin team to
                    configure this service type.
                  </p>
                </section>
              )}
            </div>

            {showDecisionZone && (
              <div className="vp-decision-zone">
                {renderDecision()}
              </div>
            )}
          </div>
        )}
      </div>

      {/* FaceMatch decision controls live in the footer (TransactionModal).
          Once decided, show the read-only status badge below the images. */}
      {isFaceMatch && isDecided && (
        <div className="vp-decision-zone vp-decision-zone-decided">
          {renderDecision()}
        </div>
      )}
    </div>
  );
};